import { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { Box, ButtonPrimary } from '@comeon/ui';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { selectBrand } from '~/components/brand/ducks/selectors.js';
import Icon, { ICON_CHAT_BUBBLE_LINE } from '~/components/icon/index.js';
import useContextualPathTransformer from '../../../../hooks/use-contextual-path-transformer.js';
import BinaryQuestion from '../../../binary-question';
import useTranslations from '../../../translation/use-translations';
import { HeadWrapper, MainTitle, SubTitle } from '../../../misc/section-head.js';
import { selectDisplayName, selectProfileFetched } from '../ducks/we-spin-selectors.js';
import { WE_SPIN_CHAT_ROOM_TEXT_KEYS } from '../constants/we-spin-text-keys.js';
import UpdateDisplayName from '../components/update-display-name.js';
import { ROUTE_LOGIN } from '../../../router/constants.js';
import { UlList, getChatTermsAccepted, storeChatTermsAccepted } from '../components/we-spin-chat-utilities.js';
import { useLocation } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import { selectCurrentEvent } from '../ducks/we-spin-home-game-duck';
import { LIVE_STATES } from '../constants/we-spin-stream-states';

const WeSpinGameRoomChat = props => {
	const router = useRouter();
	const location = useLocation();
	const authenticated = useSelector(authenticationSelector);
	const brand = useSelector(selectBrand);
	const streamView = useSelector(selectCurrentEvent);
	const displayName = useSelector(selectDisplayName);
	const isProfileFetched = useSelector(selectProfileFetched);
	const isOnline = LIVE_STATES.includes(streamView?.state);

	const [chatTermsAccepted, setChatTermsAccepted] = useState(getChatTermsAccepted());

	const acceptChatTerms = useCallback(() => {
		storeChatTermsAccepted();
		setChatTermsAccepted('true');
	}, [setChatTermsAccepted]);

	const transformPath = useContextualPathTransformer();
	const onClickLogin = useCallback(() => {
		// TODO (navigation) - use search params or redux for state
		router.push(transformPath(ROUTE_LOGIN), {
			from: location,
			implicit: true
		});
	}, [location, router, transformPath]);

	let url;
	try {
		url = streamView?.chatUrl && new URL(streamView?.chatUrl);
		url.searchParams.set('brand', brand.siteCode);
		url.searchParams.set('username', displayName);
	} catch (err) {
		url = null;
	}

	const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_CHAT_ROOM_TEXT_KEYS);

	if (isLoadingTranslations) return null;

	const hasStarted = url && isOnline;

	if (!authenticated) {
		return (
			<>
				<HeadWrapper>
					<Icon type={ICON_CHAT_BUBBLE_LINE} />
					<MainTitle>{tr.WE_SPIN_CHAT_ROOM_NOT_LOGGED_IN_TITLE}</MainTitle>
					<SubTitle>{tr.WE_SPIN_CHAT_ROOM_NOT_LOGGED_IN_TEXT}</SubTitle>
				</HeadWrapper>
				{props.buttonRef.current &&
					ReactDOM.createPortal(
						<Box flex="1" mb="0" p="5">
							<ButtonPrimary onClick={onClickLogin}>{tr.WE_SPIN_CHAT_ROOM_LOGIN_BUTTON}</ButtonPrimary>
						</Box>,
						props.buttonRef.current
					)}
			</>
		);
	}
	return (
		<>
			{!hasStarted && (
				<HeadWrapper>
					<Icon type={ICON_CHAT_BUBBLE_LINE} />
					<MainTitle>{tr.WE_SPIN_CHAT_ROOM_NOT_OPEN_TITLE}</MainTitle>
					<SubTitle>{tr.WE_SPIN_CHAT_ROOM_NOT_OPEN_TEXT}</SubTitle>
				</HeadWrapper>
			)}

			{!chatTermsAccepted && (
				<BinaryQuestion
					shouldOpenBinary={!chatTermsAccepted}
					title={tr.WE_SPIN_CHAT_ROOM_ACCEPT_TERMS_TITLE}
					text=""
					yesText={tr.WE_SPIN_CHAT_ROOM_ACCEPT_TERMS_ACCEPT_BUTTON}
					onYesClick={acceptChatTerms}
					renderInParent
					openInSidebar
				>
					<div>
						<UlList className="grid-u-sm-1" bullets={tr.WE_SPIN_CHAT_ROOM_ACCEPT_TERMS_BULLETS} />
					</div>
					<div className="u-mt2">{tr.WE_SPIN_CHAT_ROOM_ACCEPT_TERMS_FOOTER}</div>
				</BinaryQuestion>
			)}
			{isProfileFetched && !displayName && chatTermsAccepted && (
				<UpdateDisplayName onCancel={props.gotoDefaultView} onDone={() => {}} />
			)}
			{displayName && hasStarted && chatTermsAccepted && url && (
				<div className="chat-iframe-container u-flex u-flex-1">
					<iframe title="chat iframe" width="100%" src={url.toString()} />
				</div>
			)}
		</>
	);
};

export default WeSpinGameRoomChat;
